import React from 'react';
import AIProfile from '../common-layout-components/AIProfile';
import Conversation from '../common-layout-components/Conversation';
import MicControls from '../common-layout-components/MicControls';
import WebCam from '../common-layout-components/WebCam';
import ProblemStatement from '../common-layout-components/ProblemStatement';
import MobileDeviceStatus from '../common-layout-components/MobileDeviceStatus';
import MobileCameraOverlay, { type MobileCameraDetectionSnapshot } from '@/components/interview/MobileCameraOverlay';
import type { IMessage } from '../../types';
import type { HandDetector } from '@tensorflow-models/hand-pose-detection';
import type { ObjectDetection } from '@tensorflow-models/coco-ssd';

type MicMode = "listening" | "processing" | "ai_speaking" | "empty_audio" | "idle";
type AiState = "PROCESSING" | "LISTENING" | "SPEAKING" | "AI_RETRYING" | "CODING_EXERCISE" | "IMPROVING_SPEECH" | "AWAIT_SUBMIT";

export interface Phase1LayoutProps {
  // AI Profile props
  aiInterviewState: AiState;
  isAiRetrying?: boolean;

  // Conversation props
  messages: IMessage[];
  streamingText: string;
  isStreaming: boolean;
  candidateName?: string;

  // Mic Controls props
  micMode: MicMode;
  analyzeText: string;
  liveTranscript: string;
  noticeText?: string;
  onDone: () => void;

  // Problem Statement props
  problemData: {
    title: string;
    description: string;
    timeLimit: string;
  } | null;

  // Mobile Camera props
  mobileStream: MediaStream | null;
  latestMobileStreamKey: string;
  detectionModels: {
    handDetector: HandDetector | null;
    objectDetector: ObjectDetection | null;
    error: Error | null;
    hasError: boolean;
    isPreparing: boolean;
    statusMessage: string | null;
  };
  onMobileDetection: (snapshot: MobileCameraDetectionSnapshot) => void;
  workspaceDetected: boolean;
  handsDetected: number;

  // Layout options
  isMobileWebcamRequired?: boolean;
}

/**
 * Phase 1 Layout - Problem Understanding Q&A Phase
 * 
 * Full layout containing:
 * - Left: AI Profile, Conversation, Mic Controls
 * - Right: WebCam, Problem Statement, Mobile Camera Overlay
 */
const Phase1Layout: React.FC<Phase1LayoutProps> = ({
  aiInterviewState,
  isAiRetrying = false,
  messages,
  streamingText,
  isStreaming,
  candidateName,
  micMode,
  analyzeText,
  liveTranscript,
  noticeText,
  onDone,
  problemData,
  mobileStream,
  latestMobileStreamKey,
  detectionModels,
  onMobileDetection,
  workspaceDetected,
  handsDetected,
  isMobileWebcamRequired = true,
}) => {
  return (
    <React.Fragment>
      {/* Left Column: AI Profile, Conversation/Tools, Mic Controls */}
      <div className="max-w-[45%] flex flex-col overflow-hidden flex-1 min-h-0 min-w-0">
        <div className="flex flex-1 min-h-0 flex-col gap-5 md:gap-2 lg:gap-2.5 xl:gap-4">
          <div className="flex-2 min-h-0 flex">
            <AIProfile interviewState={aiInterviewState} isAiRetrying={isAiRetrying} />
          </div>
          <div className="flex-[5.5] min-h-0 flex min-w-0 overflow-hidden">
            <div className="flex-1 min-h-0 min-w-0">
              <Conversation
                messages={messages}
                responseText={streamingText}
                isStreaming={isStreaming}
                candidateName={candidateName}
              />
            </div>
          </div>
          {isMobileWebcamRequired && (
            <div className="flex-[2.5] min-h-0 flex items-center justify-center rounded-xl border border-slate-200 bg-white">
              <MicControls
                mode={micMode}
                analyzeText={analyzeText}
                liveTranscript={liveTranscript}
                onDone={onDone}
                noticeText={noticeText}
                disabled={micMode === "ai_speaking" || micMode === "idle"}
                showInlineTranscript
              />
            </div>
          )}
        </div>
      </div>

      {/* Right Column: Problem Statement and mobile overlay */}
      <div className="max-w-[55%] flex flex-col overflow-hidden flex-1 min-h-0 min-w-0 relative">
        <div className="flex flex-1 min-h-0 flex-col gap-5 md:gap-2 lg:gap-2.5 xl:gap-4">
          {/* Problem Statement */}
          <div className="flex-5 min-h-0 flex overflow-hidden">
            <div className="flex-1 min-h-0 flex overflow-hidden rounded-xl bg-white shadow-sm">
              {problemData ? (
                <div className="flex-1 overflow-y-auto">
                  <ProblemStatement
                    instructionVisible={true}
                    problemStatement={problemData.description}
                    problemTitle={problemData.title}
                  />
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  Loading problem...
                </div>
              )}
            </div>
          </div>
          {isMobileWebcamRequired ? (
            <div className="flex-5 min-h-0 min-w-0 flex overflow-hidden">
              <div className="flex flex-1 min-h-0 min-w-0 gap-4 rounded-xl bg-white p-0 shadow-sm overflow-hidden">
                <div className="flex-3 min-h-0 min-w-0">
                  <div className="relative h-full w-full overflow-hidden rounded-lg bg-black">
                    {/* Mobile Camera */}
                    {mobileStream ? (
                      <>
                        <div className="absolute inset-0 overflow-hidden">
                          <div className="w-full h-[167%] -translate-y-[40%]">
                            <MobileCameraOverlay
                              key={latestMobileStreamKey}
                              stream={mobileStream}
                              handDetector={detectionModels.handDetector}
                              objectDetector={detectionModels.objectDetector}
                              detectionError={detectionModels.error}
                              enableDetections={!detectionModels.hasError}
                              flipHorizontal={false}
                              containerClassName="h-full w-full"
                              videoClassName="h-full w-full object-cover"
                              onDetectionUpdate={onMobileDetection}
                              mode="interview"
                            />
                          </div>
                        </div>

                        {/* Minimal Status Overlay */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
                          <MobileDeviceStatus
                            mobileCameraStream={mobileStream}
                            workspaceDetected={workspaceDetected}
                            handsDetected={handsDetected > 0}
                          />
                        </div>

                        {detectionModels.isPreparing && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white">
                            <div className="mb-3 h-10 w-10 animate-spin rounded-full border-b-2 border-white" />
                            <p className="text-sm">{detectionModels.statusMessage || "Preparing workspace checks…"}</p>
                          </div>
                        )}
                        {detectionModels.error && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-900/60 px-4 text-center text-white">
                            <p className="text-sm font-semibold">Unable to run detection</p>
                            <p className="text-xs opacity-80">{detectionModels.error.message}</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/80">
                        <div className="h-12 w-12 animate-spin rounded-full border-2 border-white/30 border-t-transparent" />
                        <p className="text-sm">Waiting for phone camera…</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex min-h-0 min-w-0 flex-2 flex-col gap-4 border border-slate-200 rounded-lg p-4 justify-center items-center">
                  <WebCam candidateName={candidateName} variant="round" />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-5 min-h-0 min-w-0 flex overflow-hidden">
              <div className="flex flex-1 min-h-0 min-w-0 rounded-xl bg-white p-4 shadow-sm">
                <MicControls
                  mode={micMode}
                  analyzeText={analyzeText}
                  liveTranscript={liveTranscript}
                  onDone={onDone}
                  noticeText={noticeText}
                  disabled={micMode === "ai_speaking" || micMode === "idle"}
                  showInlineTranscript
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Phase1Layout;
